import { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { configuracoesApi } from "../../hooks/configuracoesApi";
import { cuponsApi } from "../../hooks/cuponsApi";
import { boolean, copy, deepMerge } from "../../services/Functions";
import Configuracoes from "../../types/Configuracoes";
import { FreteEspecial } from "../../types/FreteEspecial";
import { FreteGratis } from "../../types/FreteGratis";
import { EmpresaContext } from "../Empresa/EmpresaContext";
import { ConfiguracoesContext } from "./ConfiguracoesContext";
import {
  ConfiguracoesJson,
  politica_default,
  politica_frete_default,
  politicaTrocaeDevolucao_default,
} from "./ConfiguracoesJson";

export const ConfiguracoesProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const api = configuracoesApi();
  const cupomApi = cuponsApi();
  const [loading, setLoading] = useState<boolean>(true);

  const { empresa, setEmpresa } = useContext(EmpresaContext);

  const [configuracoes, setConfiguracoes] = useState<Configuracoes | null>(
    null
  );

  const [configuracoesDefault, setConfiguracoesDefault] =
    useState<Configuracoes | null>(null);
  const [erp_modificado, setErp_modificado] = useState<boolean>(false);
  const [fretes_especiais, setFretes_especiais] = useState<
    Array<FreteEspecial>
  >([]);
  const [fretes_gratis, setFretes_gratis] = useState<Array<FreteGratis>>([]);
  const [cupons, setCupons] = useState<any>([]);
  const [politica_de_privacidade, setPolitica_de_privacidade] =
    useState<string>(politica_default);
  const [politica_de_troca_e_devolucao, setPolitica_de_troca_e_devolucao] =
    useState<string>(politicaTrocaeDevolucao_default);
  const [politica_de_frete, setPolitica_de_frete] =
    useState<string>(politica_frete_default);
  const [sugestoesCores, setSugestoesCores] = useState<{
    cor_primaria?: string;
    cor_secundaria?: string;
    cores_sugeridas?: Array<{ cor_primaria: string; cor_secundaria: string; descricao?: string }>;
  } | null>(null);
  const [loadingSugestoes, setLoadingSugestoes] = useState<boolean>(false);
  const [mostrarSugestoes, setMostrarSugestoes] = useState<boolean>(false);
  const [logoExistente, setLogoExistente] = useState<boolean>(false);
  const [previewCores, setPreviewCores] = useState<{
    cor_primaria: string;
    cor_secundaria: string;
  } | null>(null);

  const requestConfiguracoes = async () => {
    const response = await api.select_configuracoes_edicao();
    const response_cupons = await cupomApi.getCupons();
    if (response.configuracoes) {
      deepMerge(response.configuracoes, ConfiguracoesJson);
      setConfiguracoes(response.configuracoes);
      setConfiguracoesDefault(response.configuracoes);
      setFretes_especiais(response.fretes_especiais);
      setFretes_gratis(response.fretes_gratis);
      setPolitica_de_privacidade(
        response.politica_de_privacidade === null
          ? politica_default
          : response.politica_de_privacidade
      );
      setPolitica_de_troca_e_devolucao(
        response.politica_de_troca_e_devolucao === null
          ? politicaTrocaeDevolucao_default
          : response.politica_de_troca_e_devolucao
      );
      setPolitica_de_frete(
        response.politica_de_frete === null
          ? politica_frete_default
          : response.politica_de_frete
      );
      setCupons(response_cupons.data);
    }
  };

  const salvarConfiguracoes = async () => {
    setLoading(true);
    if (configuracoes === null) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Erro ao inicializar as configurações!",
      });
      return false;
    }

    setConfiguracoes({ ...configuracoes, primeiro_acesso: false });

    const foramEditadas = foramFeitasEdicoes(
      configuracoesDefault,
      configuracoes
    );

    const newEmpresa = {
      configuracoes: { ...configuracoes, primeiro_acesso: false },
      fretes_especiais: fretes_especiais,
      fretes_gratis: fretes_gratis,
      politica_de_privacidade: politica_de_privacidade,
      cupons: cupons,
      politica_de_troca_e_devolucao: politica_de_troca_e_devolucao,
      politica_de_frete: politica_de_frete,
      erp_modificado: foramEditadas,
    };
    try {
      await api.salvar_configuracoes(newEmpresa);

      await requestConfiguracoes();

      setLoading(false);

      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Erro ao salvar as configurações!",
      });
      setConfiguracoes({ ...configuracoes, primeiro_acesso: true });
      return false;
    }
  };

  const processarLogo = async (base64: string, removerFundo: boolean = false) => {
    try {
      setLoading(true);
      const newLogo: string = await api.processar_logo(base64, removerFundo);
      if (configuracoes) {
        setConfiguracoes({
          ...configuracoes,
          dados_empresa: {
            ...configuracoes.dados_empresa,
            informacoes_gerais: {
              ...configuracoes.dados_empresa.informacoes_gerais,
              logotipo: newLogo,
            },
          },
        });
      }

      // Remover sugestões automáticas - apenas salvar o logo
      setMostrarSugestoes(false);
      setSugestoesCores(null);

      setLoading(false);
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Erro ao processar a logo!",
      });
      setLoading(false);
    }
  };

const clamp = (v: number, min = 0, max = 100) => Math.min(max, Math.max(min, v));

const hexToRgb = (hex: string) => {
  const s = hex.replace('#', '').trim();
  const b = s.length === 3 ? s.split('').map(ch => ch + ch).join('') : s;
  const n = parseInt(b, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = (r: number, g: number, b: number) => {
  const h = (x: number) => x.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
};

const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToRgb = (h: number, s: number, l: number) => {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1/3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1/3);
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
};

const hexToHsl = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
};

const hslToHex = (h: number, s: number, l: number) => {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
};

const multLightness = (hex: string, factor: number) => {
  const { h, s, l } = hexToHsl(hex);
  const l2 = clamp(l * factor, 0, 100);
  return hslToHex(h, s, l2);
};

const rotateHue = (hex: string, deg: number) => {
  const { h, s, l } = hexToHsl(hex);
  return hslToHex((h + deg) % 360, s, l);
};

const contrasteRatio = (c1: string, c2: string) => {
  const lum = (hex: string) => {
    const { r, g, b } = hexToRgb(hex);
    const norm = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * norm[0] + 0.7152 * norm[1] + 0.0722 * norm[2];
  };
  const L1 = lum(c1), L2 = lum(c2);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
};

const obterSugestoesCores = async (logoBase64?: string) => {
  try {
    setLoadingSugestoes(true);
    const logo = logoBase64 || configuracoes?.dados_empresa?.informacoes_gerais?.logotipo;
    if (!logo) {
      setLoadingSugestoes(false);
      return;
    }

    const resposta = await api.sugerirCoresLogoIA(logo);

    if (resposta && !resposta.error) {
      const sugestoes = { cores_sugeridas: [] as Array<any> };

      if (resposta.cor_primaria) {
        // 1 - Sugestão
        sugestoes.cores_sugeridas.push({
          tipo: "Base",
          cor_primaria: resposta.cor_primaria,
          cor_secundaria: resposta.cor_secundaria,
        });

        let primariaEscurecida = multLightness(resposta.cor_primaria, 0.5);
        let secundariaMaisClara = multLightness(resposta.cor_secundaria, 1.5);

        if (contrasteRatio(primariaEscurecida, secundariaMaisClara) < 4.5) {
          primariaEscurecida = multLightness(resposta.cor_primaria, 0.3);
          secundariaMaisClara = multLightness(resposta.cor_secundaria, 1.8);
        }

        // 2 - Sugestão
        sugestoes.cores_sugeridas.push({
          tipo: "Ajustada",
          cor_primaria: primariaEscurecida,
          cor_secundaria: secundariaMaisClara,
          cor_analoga: rotateHue(secundariaMaisClara, 10), // análoga em +30'°
        });

        // 3 - Sugestão
        sugestoes.cores_sugeridas.push({
          tipo: "Monocromática",
          cor_primaria: resposta.cor_primaria,
          cor_secundaria: multLightness(resposta.cor_primaria, 2.7), // ou 1.3
        });

        // 4 - Sugestão - Monocromática com contraste
        let cor4Primaria = resposta.cor_secundaria;
        const hsl4 = hexToHsl(resposta.cor_secundaria);

        // Se a cor for clara (lightness > 50), escurecer; se escura, clarear
        let cor4Secundaria = hsl4.l > 50
          ? multLightness(resposta.cor_secundaria, 0.2)  // Escurece cores claras
          : multLightness(resposta.cor_secundaria, 2.5); // Clareia cores escuras

        // Validar contraste mínimo de 4.5:1 (WCAG AA)
        if (contrasteRatio(cor4Primaria, cor4Secundaria) < 4.5) {
          // Ajustar mais drasticamente
          cor4Secundaria = hsl4.l > 50
            ? multLightness(resposta.cor_secundaria, 0.15) // Escurece mais
            : multLightness(resposta.cor_secundaria, 3.0); // Clareia mais
        }

        if (cor4Secundaria === '#000000' || cor4Secundaria === '#FFFFFF') {
          const hslPrimaria = hexToHsl(resposta.cor_primaria);
          cor4Primaria = resposta.cor_primaria;
          cor4Secundaria = hslPrimaria.l > 50
            ? multLightness(resposta.cor_primaria, 0.2)
            : multLightness(resposta.cor_primaria, 2.5);
        }

        sugestoes.cores_sugeridas.push({
          tipo: "Monocromática",
          cor_primaria: cor4Primaria,
          cor_secundaria: cor4Secundaria,
        });

      }

      setSugestoesCores(sugestoes);
    }

    setLoadingSugestoes(false);
  } catch (error) {
    console.log("Erro ao obter sugestões de cores:", error);
    setLoadingSugestoes(false);
  }
};




  const previewCor = (cor_primaria: string, cor_secundaria: string) => {
    // Preview é instantâneo - sem loading
    setPreviewCores({ cor_primaria, cor_secundaria });
  };

  const aplicarSugestaoCor = (cor_primaria: string, cor_secundaria: string) => {
    if (configuracoes) {
      setConfiguracoes({
        ...configuracoes,
        catalogo: {
          ...configuracoes.catalogo,
          cor_primaria,
          cor_secundaria,
        },
      });
      // Limpar preview e ocultar sugestões após aplicar
      setPreviewCores(null);
      setMostrarSugestoes(false);
      setSugestoesCores(null);
    }
  };

  const rejeitarSugestoes = () => {
    setPreviewCores(null);
    setMostrarSugestoes(false);
    setSugestoesCores(null);
  };

  const limparPreview = () => {
    setPreviewCores(null);
  };

  function foramFeitasEdicoes(configuracoesDefault, configuracoesEditadas) {
    // Função interna para verificar as mudanças em um objeto específico
    function verificarMudancas(objDefault, objEditado) {
      for (let chave in objDefault) {
        if (Array.isArray(objDefault[chave])) {
          if (objDefault[chave].length !== objEditado[chave].length) {
            return true;
          }
          for (let i = 0; i < objDefault[chave].length; i++) {
            if (objDefault[chave][i] !== objEditado[chave][i]) {
              return true;
            }
          }
        } else {
          if (objDefault[chave] !== objEditado[chave]) {
            return true;
          }
        }
      }
      return false;
    }

    let mudancasBling = false;
    let mudancasBlingV2 = false;

    if (configuracoesDefault === null || configuracoesEditadas === null)
      return false;

    mudancasBling = verificarMudancas(
      configuracoesDefault.dados_bling,
      configuracoesEditadas.dados_bling
    );

    if (configuracoesDefault.dados_bling_v2) {
      mudancasBlingV2 = verificarMudancas(
        configuracoesDefault.dados_bling_v2,
        configuracoesEditadas.dados_bling_v2
      );
    }

    // Verifica as mudanças em dados_tiny_v2
    const mudancasTinyV2 = verificarMudancas(
      configuracoesDefault.dados_tiny_v2,
      configuracoesEditadas.dados_tiny_v2
    );

    // Retorna true se houve mudanças em qualquer um dos objetos, caso contrário, retorna false
    return mudancasBling || mudancasTinyV2 || mudancasBlingV2;
  }

  // Verificar se logo já existe no backend
  const verificarLogoExistente = async () => {
    try {
      const resultado = await api.verificarLogoExistente();
      const temLogo = resultado && resultado.length > 0 && resultado[0] && 
                     (typeof resultado[0] === 'string' ? resultado[0].length > 0 : 
                      resultado[0]['configuracoes->\'$.dados_empresa.informacoes_gerais.logotipo\''] !== null);
      setLogoExistente(temLogo);
      // Se NÃO tem logo, pode mostrar sugestões automaticamente
      if (!temLogo) {
        setMostrarSugestoes(false); // Aguardar upload
      }
    } catch (error) {
      console.log('Erro ao verificar logo existente:', error);
      setLogoExistente(false);
    }
  };

  const init = async () => {
    if (
      localStorage.getItem("configuracoes") &&
      localStorage.getItem("fretes_gratis") &&
      localStorage.getItem("fretes_especiais") &&
      localStorage.getItem("politica_de_privacidade") &&
      localStorage.getItem("politica_de_troca_e_devolucao")
    ) {
      // se já tiver as configurações salvas no localStorage pegar elas, vem do stepper de modais de configs inicias
      setConfiguracoes(JSON.parse(localStorage.getItem("configuracoes")!));
      setFretes_especiais(
        JSON.parse(localStorage.getItem("fretes_especiais")!)
      );
      setFretes_gratis(JSON.parse(localStorage.getItem("fretes_gratis")!));
      setPolitica_de_privacidade(
        localStorage.getItem("politica_de_privacidade")!
      );
      setPolitica_de_troca_e_devolucao(
        localStorage.getItem("politica_de_troca_e_devolucao")!
      );
    } else {
      await requestConfiguracoes();
    }
    
    // Verificar logo existente após carregar configurações
    await verificarLogoExistente();
    
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  // transfomar campos do pedido em obrigatórios se o intermediador de pagamento ou o frete estiverem ativos
  useEffect(() => {
    if (configuracoes === null) return;

    let mudancasNecessarias = false;
    const copiaConfig = copy(configuracoes);

    const camposPagamento = [
      "email",
      "telefone",
      "cep",
      "estado",
      "cidade",
      "endereco",
      "numero",
      "bairro",
    ];
    const camposFrete = [
      "cep",
      "estado",
      "cidade",
      "endereco",
      "numero",
      "bairro",
    ];

    const pagamentoAtivo =
      boolean(configuracoes.intermediadores.pagarme) ||
      boolean(configuracoes.intermediadores.pagseguro)
        ? true
        : false;
    const freteAtivo =
      boolean(configuracoes.frete.correiosAtivo) ||
      boolean(configuracoes.frete.freteEspecialAtivo) ||
      boolean(configuracoes.frete.freteGratisAtivo) ||
      boolean(configuracoes.frete.melhorEnvio.ativo) ||
      boolean(configuracoes.frete.entregaLocal.ativo)
        ? true
        : false;

    const atualizarCampos = (campos, camposAlvo, ativo) => {
      campos
        .filter((campo) => camposAlvo.includes(campo.campo))
        .forEach((campo) => {
          if (campo.obrigatorio !== ativo) {
            campo.obrigatorio = ativo;
            mudancasNecessarias = true;
          }
          if (ativo && !campo.ativo) {
            campo.ativo = true;
            mudancasNecessarias = true;
          }
        });
    };

    [
      copiaConfig.campos_pedido_geral,
      copiaConfig.campos_pedido_pessoa_fisica,
      copiaConfig.campos_pedido_pessoa_juridica,
    ].forEach((campos) => {
      atualizarCampos(campos, camposPagamento, pagamentoAtivo);
      atualizarCampos(campos, camposFrete, freteAtivo || pagamentoAtivo);
    });

    if (!configuracoes.pagamentoOnlineAtivo) {
      const intermediadoresAtivos = ['pagarme', 'pagseguro', 'mercadopago', 'pagarmehub'];
      
      intermediadoresAtivos.forEach(intermediador => {
        if (copiaConfig.intermediadores[intermediador]) {
          if (copiaConfig.intermediadores[intermediador].pix?.ativo) {
            copiaConfig.intermediadores[intermediador].pix.ativo = false;
            mudancasNecessarias = true;
          }
          if (copiaConfig.intermediadores[intermediador].cartao?.ativo) {
            copiaConfig.intermediadores[intermediador].cartao.ativo = false;
            mudancasNecessarias = true;
          }
        }
      });
    }

    if (mudancasNecessarias) {
      setConfiguracoes(copiaConfig);
    }
  }, [
    configuracoes?.frete.correiosAtivo,
    configuracoes?.frete.freteEspecialAtivo,
    configuracoes?.frete.melhorEnvio.ativo,
    configuracoes?.frete.freteGratisAtivo,
    configuracoes?.frete.entregaLocal.ativo,
    configuracoes?.pagamentoOnlineAtivo,
    configuracoes?.pagamentoCombinarAtivo,
  ]);

  return (
    <ConfiguracoesContext.Provider
      value={{
        configuracoes: configuracoes || ConfiguracoesJson,
        setConfiguracoes,
        fretes_especiais,
        processarLogo,
        setFretes_especiais,
        fretes_gratis,
        cep_gratis: fretes_gratis,
        setFretes_gratis,
        politica_de_privacidade,
        setPolitica_de_privacidade,
        politica_de_troca_e_devolucao,
        setPolitica_de_troca_e_devolucao,
        politica_de_frete,
        setPolitica_de_frete,
        salvarConfiguracoes,
        requestConfiguracoes,
        loading,
        setLoading,
        cupons,
        setCupons,
        sugestoesCores,
        setSugestoesCores,
        loadingSugestoes,
        mostrarSugestoes,
        setMostrarSugestoes,
        logoExistente,
        previewCores,
        verificarLogoExistente,
        obterSugestoesCores,
        previewCor,
        aplicarSugestaoCor,
        rejeitarSugestoes,
        limparPreview,
      }}
    >
      {children}
    </ConfiguracoesContext.Provider>
  );
};
